import {
    gteMinAge,
    isCustomerNumber,
    isNumeric,
    isPastDate,
    isRFC5322Email,
    isValidDateInput,
    isValidSwitchingDate,
    isZip,
    lteMaxAge,
    preventedIsCustomerNumberUnknownInAAX,
    preventedIsValidIban,
    preventedIsValidLandlineAreaCode,
    preventedIsValidMobileAreaCode,
    startsWith,
} from '../validators';
import yupLocale from './yupLocale';
import { errorTypes } from '../settings';

export function testNumeric(options) {
    const { message = `\${label} muss aus Ziffern bestehen.`, excludeEmptyString = false } = options || {};

    return this.test(errorTypes.NUMERIC, message, (value) => {
        if (excludeEmptyString && !value) {
            return true;
        }
        return isNumeric(value);
    });
}

export function testOptionalMin(min, message) {
    const errMessage = message || `\${label} muss mindestens ${min} Zeichen lang sein.`;
    const regex = new RegExp(`.{${min},}`);

    return this.test(errorTypes.MIN, errMessage, (value) => {
        if (!value) {
            return true;
        }

        return regex.test(value);
    });
}

export function testOptionalOneOfString(allowedValues, message = yupLocale.mixed.required) {
    return this.oneOf([...allowedValues, '', undefined], message);
}

export function testOptionalOneOfNumber(allowedValues, message = yupLocale.mixed.required) {
    return this.oneOf([...allowedValues, undefined], message);
}

export function testStartsWith(prefix, options) {
    const { message = `\${label} muss mit "${prefix}" beginnen.`, excludeEmptyString = false } = options || {};

    return this.test(errorTypes.STARTS_WITH, message, (value) => {
        if (excludeEmptyString && !value) {
            return true;
        }

        return startsWith(prefix, value);
    });
}

export function testNotStartsWith(prefix, options) {
    const { message = `\${label} darf nicht mit "${prefix}" beginnen.`, excludeEmptyString = false } = options || {};

    return this.test(errorTypes.NOT_STARTS_WITH, message, (value) => {
        if (excludeEmptyString && !value) {
            return true;
        }

        return !startsWith(prefix, value);
    });
}

export function testValidDate(options) {
    const { message = `\${label} ist ungültig.`, excludeEmptyString = false } = options || {};

    return this.test(errorTypes.VALID_DATE, message, (value) => {
        if (excludeEmptyString && !value) {
            return true;
        }

        return isValidDateInput(value);
    });
}

export function testDateNotInFuture(options) {
    const { message = `\${label} darf nicht in der Zukunft liegen.`, excludeEmptyString = false } = options || {};

    return this.test(errorTypes.NOT_IN_FUTURE, message, (value) => {
        if (excludeEmptyString && !value) {
            return true;
        }
        return isPastDate(value);
    });
}

export function testMinAge(age, options) {
    const { message = `\${label} entspricht nicht dem Mindestalter (${age} Jahre).`, excludeEmptyString = false } =
        options || {};

    return this.test(errorTypes.MIN_AGE, message, (value) => {
        if (excludeEmptyString && !value) {
            return true;
        }
        return gteMinAge(value, age);
    });
}

export function testMaxAge(age, options) {
    const { message = `\${label} liegt zu weit in der Vergangenheit.`, excludeEmptyString = false } = options || {};

    return this.test(errorTypes.MAX_AGE, message, (value) => {
        if (excludeEmptyString && !value) {
            return true;
        }
        return lteMaxAge(value, age);
    });
}

export function testSwitchingDate(options) {
    const { message = `\${label} ist kein gültiger Schaltungstermin.`, excludeEmptyString = false } = options || {};

    return this.test(errorTypes.SWITCHING_DATE, message, (value, testContext) => {
        if (excludeEmptyString && !value) {
            return true;
        }

        const { context } = testContext.options;

        if (!context?.switchingDates) {
            return false;
        }

        return isValidSwitchingDate(value, context.switchingDates);
    });
}

export function testZip(options) {
    const { message = `\${label} ist ungültig.`, excludeEmptyString = false } = options || {};

    return this.test(errorTypes.ZIP, message, (value) => {
        if (excludeEmptyString && !value) {
            return true;
        }
        return isZip(value);
    });
}

export function testAwbEmail(options) {
    const { message = yupLocale.string.email, excludeEmptyString = false } = options || {};

    return this.test(errorTypes.RFC_EMAIL, message, (value) => {
        if (excludeEmptyString && !value) {
            return true;
        }

        return isRFC5322Email(value);
    });
}

export function testMobileAreaCode(options) {
    const { message = `\${label} ist ungültig.`, excludeEmptyString = false } = options || {};

    return this.test(errorTypes.AREA_CODE, message, async (value, testContext) => {
        if (excludeEmptyString && !value) {
            return true;
        }

        try {
            return await preventedIsValidMobileAreaCode(value);
        } catch {
            return testContext.createError({ message: `\${label} kann gerade nicht überprüft werden.` });
        }
    });
}

export function testLandlineAreaCode(options) {
    const { message = `\${label} ist ungültig.`, excludeEmptyString = false } = options || {};
    return this.test(errorTypes.AREA_CODE, message, async (value, testContext) => {
        if (excludeEmptyString && !value) {
            return true;
        }

        try {
            return await preventedIsValidLandlineAreaCode(value);
        } catch {
            return testContext.createError({ message: `\${label} kann gerade nicht überprüft werden.` });
        }
    });
}

export function testCustomerNumber(options) {
    const { message = `\${label} muss eine 10-stellige Zahl sein.`, excludeEmptyString = false } = options || {};
    return this.test(errorTypes.CUSTOMER_NUMBER, message, (value) => {
        if (excludeEmptyString && !value) {
            return true;
        }
        return isCustomerNumber(value);
    });
}

export function testUnknownCustomerNumber(options) {
    const { message = `\${label} ist bereits in Verwendung.`, excludeEmptyString = false } = options || {};
    return this.test(errorTypes.CUSTOMER_NUMBER, message, async (value, testContext) => {
        if (excludeEmptyString && !value) {
            return true;
        }

        try {
            return await preventedIsCustomerNumberUnknownInAAX(value);
        } catch {
            return testContext.createError({ message: `\${label} kann gerade nicht überprüft werden.` });
        }
    });
}

export function testIban(options) {
    const { message = `\${label} ist ungültig.`, excludeEmptyString = false } = options || {};

    return this.test(errorTypes.IBAN, message, async (value, testContext) => {
        if (excludeEmptyString && !value) {
            return true;
        }

        const checkoutId = testContext.options.context?.checkoutId;

        if (!checkoutId) {
            return false;
        }

        try {
            return await preventedIsValidIban(value, checkoutId);
        } catch {
            return testContext.createError({ message: `\${label} kann gerade nicht überprüft werden.` });
        }
    });
}

export function testUniqueCli(message = '') {
    const propertyName = 'cli';
    return this.test(
        errorTypes.UNIQUE_CLI,
        message,
        // eslint-disable-next-line func-names
        function (value) {
            if (value[propertyName] === '') {
                return true;
            }

            if (
                this.parent
                    .filter((item) => item !== value)
                    .filter((item) => item[propertyName] !== '')
                    .some((item) => item[propertyName] === value[propertyName])
            ) {
                throw this.createError({
                    path: `${this.path}.${propertyName}`,
                    message,
                });
            }

            return true;
        }
    );
}

export function testExtendedRequired(excludedValue, message = yupLocale.mixed.required) {
    return this.test(errorTypes.REQUIRED, message, (value) => {
        if (typeof value === 'number') {
            return value !== excludedValue;
        }
        return !!value && value !== excludedValue;
    });
}
